import React, { useEffect, useState } from "react";
import SaveButton from "../../../components/Buttons/SaveButton";
import DataTable from '../../../components/DataTable/DataTable';
import { IColInfo } from "../../../components/DataTable/Interfaces";
import SelectListField from "../../../components/Fields/SelectListField";
import TextboxField from "../../../components/Fields/TextboxField";
import { FormConfig } from "../../../components/Fields/Types";
import useSelectListOptions from "../../../components/Hooks/SelectListOptionsHook/useSelectListOptions";
import useValidation from "../../../components/Hooks/ValidationHook/useValidation";
import Popup from "../../../components/Popups/Popup";
import { ApplicationFunctions, useAuth } from "../../../contexts/authContext";
import { useNotifications } from "../../../contexts/notificationContext";
import ItemRevisionColorModel from "../../../models/ItemRevisionColorModel";
import DataService from "../../../services/data.service";
import { PopupProps } from "../../../types/PopupProps";
import { Result } from "../../../types/Result";

interface IProps extends PopupProps {
    itemRevisionId: number
}

const ItemSizesPopup: React.FC<IProps> = ({ show, onHide, itemRevisionId }) => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const [tableData, setTableData] = useState<ItemRevisionColorModel[]>();
    const [showAddColor, setShowAddColor] = useState(false);
    const { handleResult, handleException } = useNotifications();
    const { isAuthorizedFor } = useAuth();

    const colors = useSelectListOptions('api/v1/selectlist/Colors?activeOnly=true', '-- Select color');

    const [formData, setFormData] = useState<ItemRevisionColorModel>({
        itemRevisionId: itemRevisionId,
        colorId: 0
    } as ItemRevisionColorModel);

    useEffect(() => {
        getTableData();
    }, [itemRevisionId]);

    function getTableData() {
        DataService.get<ItemRevisionColorModel[]>('api/v1/ItemRevisionColors/ByItemRevision/' + itemRevisionId)
            .then(response => {
                setTableData(response);
            })
            .catch(e => { handleException(e); });
    }

    function deleteRow(rowData) {
        DataService.delete('api/v1/ItemRevisionColors/', rowData.id)
            .then(response => {
                handleResult(response);
                getTableData();
            })
            .catch(e => { handleException(e); });
    }

    function getColorCode(itemRevisionColor: ItemRevisionColorModel, colInfo: IColInfo): string | undefined {
        return itemRevisionColor.color.colorCode;
    }

    const colInfos: IColInfo[] = [
        { key: true, dataProp: 'Id', visible: false },
        { title: 'Color', dataProp: 'color.name', sort: 'asc', getCellBackgroundColor: getColorCode },
        { title: 'Alt. Name', dataProp: 'alternativeName' },
        { title: 'Alt. Code', dataProp: 'alternativeColorCode' },
    ];

    if (isAuthorizedFor(ApplicationFunctions.UpdateItemRevision)) {
        colInfos.push(
            {
                headerButtons: [{ text: "Add", icon: 'fa-plus-square', color: '#26A69A', onClick: () => setShowAddColor(true) }],
                rowButtons: [{ text: "Delete", icon: 'fa-trash', color: '#F3565D', onClick: deleteRow }],
            });
    }

    function submit() {
        if (formData) {
            DataService
                .create<number>(`api/v1/ItemRevisionColors`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true) {
                        setShowAddColor(false);
                        getTableData();
                    }
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'colorId', label: 'Color', labelSize: 2, validationRules: [{ type: 'isRequired' }] },
            { name: 'alternativeName', label: 'Alternative name', labelSize: 2 },
            { name: 'alternativeColorCode', label: 'Alternative code', labelSize: 2 },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    return (
        <Popup show={show} onHide={onHide} size={'xl'} title={'Colors'} >
            <Popup.Body>
                {showAddColor == true && <SelectListField {...getFieldProps('colorId')} options={colors} />}
                {showAddColor == true && <TextboxField {...getFieldProps('alternativeName')} />}
                {showAddColor == true && <TextboxField {...getFieldProps('alternativeColorCode')} />}
                {showAddColor == false && 
                    <DataTable
                        name='dtItemRevisionColors'
                        tableData={tableData}
                        colInfos={colInfos}
                    />}
            </Popup.Body>
            <Popup.Footer>
                {showAddColor && <SaveButton {...getSubmitButtonProps()} text='Add' />}
            </Popup.Footer>

        </Popup>
    );
}
export default ItemSizesPopup;